/**
 * `ShellHeaderItems` (1.I-1 provides): children portaled into the header's end region.
 *
 * Each item names its place, and the header keeps one fixed element per place in the order
 * below, so the end region reads the same however its items mount. Without places a portal
 * appended its children when they committed: the NOW chip (a lazy chunk mounted by the frame)
 * landed before or after the page's own items depending on which chunk loaded first, and moved
 * again on every page change (DECISIONS 445: the NOW popover's visual baselines differed between
 * repeats).
 */
import { createContext, createElement, type ReactNode, useContext } from 'react';
import { createPortal } from 'react-dom';

/** The end region's places, in the order they show. */
export const HEADER_PLACES = ['palette', 'page-mode', 'now', 'rail', 'toolbar'] as const;
export type HeaderPlace = (typeof HEADER_PLACES)[number];

/** The header's items element (`.cx-header__items`), holding `HeaderPlaces`. */
export const HeaderSlotContext = createContext<HTMLElement | null>(null);

/** One empty `display: contents` element per place, rendered by the header inside its items element. */
export function HeaderPlaces() {
  return HEADER_PLACES.map((place) =>
    createElement('span', {
      key: place,
      className: 'cx-header__place',
      'data-header-place': place,
    }),
  );
}

export function ShellHeaderItems({
  place,
  children,
}: {
  place: HeaderPlace;
  children?: ReactNode;
}) {
  const element = useContext(HeaderSlotContext);
  if (!element) return null;
  const target =
    element.querySelector<HTMLElement>(`:scope > [data-header-place="${place}"]`) ?? element;
  return createPortal(children, target);
}
